import {
  Box,
  Divider,
  DividerProps,
  Flex,
  Menu,
  MenuButton,
  MenuButtonProps,
  MenuItemOption,
  MenuItemOptionProps,
  MenuList,
  MenuListProps,
  MenuOptionGroup,
  MenuProps,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { ChevronDownIcon } from 'ui-components/dist/ChevronDownIcon';
import { ChevronUpIcon } from 'ui-components/dist/ChevronUpIcon';
import theme from '~/styles/theme';
import { convertPxToRems } from '~/utils-ts';

import { ISelectDropdownOption } from './SelectDropdown.types';

export interface ISelectDropdownContainerProps
  extends Omit<MenuProps, 'children'> {
  defaultValue: string;
  itemPrefix?: string;
  menuButtonProps?: MenuButtonProps;
  menuListProps?: MenuListProps;
  menuItemOptionProps?: MenuItemOptionProps;
  onChange: (value: string) => void;
  options: ISelectDropdownOption[];
  selectedPrefix?: string;
  dividerProps?: DividerProps;
  order?: number;
  trackId?: string;
}

export const SelectDropdown: React.FC<ISelectDropdownContainerProps> = ({
  defaultValue,
  itemPrefix,
  menuButtonProps,
  menuListProps,
  menuItemOptionProps,
  onChange,
  options,
  selectedPrefix,
  dividerProps,
  order,
  trackId,
  ...props
}) => {
  const [value, setValue] = useState<string>(defaultValue);
  const { friendlyValue } = options.find(({ value: v }) => v === value) ?? {};

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleChange = (value: string) => {
    onChange(value);
    setValue(value);
  };

  return (
    <Menu variant="select" size="md" {...props}>
      {({ isOpen }) => (
        <>
          <MenuButton
            position="relative"
            fontSize="md"
            lineHeight={convertPxToRems(14)}
            fontWeight={500}
            order={order}
            /**
             * NOTE: ChakraUI should be applying the variants button styles but is not since we upgraded to chakra v2.
             */
            {...theme.components?.Menu?.variants?.select?.button}
            {...menuButtonProps}
            paddingLeft={4}
            paddingRight={3}
            track-id={trackId}
          >
            <Flex
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Box>
                {selectedPrefix && (
                  <Text as="span" color="primary.300" marginRight={1}>
                    {selectedPrefix}{' '}
                  </Text>
                )}
                <Text as="span" color="primary.700">
                  {friendlyValue ?? ''}
                </Text>
              </Box>
              <Box alignSelf="right">
                {isOpen ? (
                  <ChevronUpIcon boxSize={5} />
                ) : (
                  <ChevronDownIcon boxSize={5} />
                )}
              </Box>
            </Flex>
          </MenuButton>
          <MenuList {...menuListProps}>
            <MenuOptionGroup
              defaultValue={value}
              value={value}
              onChange={handleChange}
              type="radio"
            >
              {options.map(
                ({
                  friendlyValue,
                  value,
                  isDefault,
                  isDisabled,
                  render,
                  divider,
                  trackId,
                }) => (
                  <React.Fragment key={value}>
                    <MenuItemOption
                      defaultChecked={isDefault}
                      isDisabled={isDisabled}
                      key={value}
                      value={value}
                      onClick={() => handleChange(value)}
                      track-id={trackId}
                      // NOTE: FOAM-160 Remove after stack upgrade and add icon={null}
                      sx={{
                        'span[class*="icon-wrapper"]': {
                          display: 'none',
                        },
                        fontWeight: value === defaultValue ? 500 : 'normal',
                      }}
                      {...menuItemOptionProps}
                    >
                      {render ? (
                        render(value === defaultValue, isDisabled)
                      ) : (
                        <>
                          {itemPrefix && <Text as="span">{itemPrefix} </Text>}
                          {friendlyValue}
                        </>
                      )}
                    </MenuItemOption>
                    {divider && (
                      <Divider
                        data-testid="select-dropdown-divider"
                        {...dividerProps}
                      />
                    )}
                  </React.Fragment>
                ),
              )}
            </MenuOptionGroup>
          </MenuList>
        </>
      )}
    </Menu>
  );
};
